import React, { useCallback, useEffect, useState } from 'react';
import { voiceInfraApi } from './voiceInfraApi';

type PlatformRow = {
  code: string;
  label: string;
  description: string;
  status: string;
};

type AuditRow = {
  id: string;
  workspace_id: string;
  old_platform_label?: string;
  new_platform_label?: string;
  old_platform_code: string;
  new_platform_code: string;
  reason?: string;
  created_at: string;
};

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-enterprise-200 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-enterprise-900">{title}</h2>
      {children}
    </div>
  );
}

export const VoicePlatformsPage: React.FC = () => {
  const [platforms, setPlatforms] = useState<PlatformRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [platRes, auditRes]: any[] = await Promise.all([
        voiceInfraApi.voicePlatforms(),
        voiceInfraApi.voicePlatformAudit({ limit: 30 }),
      ]);
      const platData = platRes?.data ?? platRes;
      setPlatforms(platData?.platforms || []);
      setAudit(auditRes?.data || auditRes || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load voice platforms');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <Panel title="Available Voice Platforms">
        <p className="mb-4 text-sm text-enterprise-600">
          Enterprise Voice Platforms are selectable per workspace. Default for all workspaces is{' '}
          <strong>Retell</strong>. Switch individual workspaces to <strong>SWS AI Calling</strong>{' '}
          from the workspace detail page — no deployment required.
        </p>
        {loading && <p className="text-sm text-enterprise-500">Loading…</p>}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          {platforms.map((p) => (
            <div
              key={p.code}
              className="rounded-xl border border-enterprise-200 bg-enterprise-50/50 p-4"
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-enterprise-900">{p.label}</h3>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                  {p.status}
                </span>
              </div>
              <p className="mt-2 text-xs text-enterprise-600">{p.description}</p>
              <p className="mt-2 font-mono text-[10px] text-enterprise-400">code: {p.code}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Voice Platform audit trail">
        <p className="mb-3 text-sm text-enterprise-600">
          Every workspace Voice Platform change is recorded with administrator, timestamp, and
          reason.
        </p>
        {audit.length === 0 ? (
          <p className="text-sm text-enterprise-500">No platform changes recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-enterprise-100 text-xs uppercase text-enterprise-500">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Workspace</th>
                  <th className="py-2 pr-4">Change</th>
                  <th className="py-2 pr-4">Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((row) => (
                  <tr key={row.id} className="border-b border-enterprise-50">
                    <td className="py-2 pr-4 text-xs text-enterprise-600">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{row.workspace_id?.slice(0, 8)}…</td>
                    <td className="py-2 pr-4">
                      {row.old_platform_label || row.old_platform_code} →{' '}
                      {row.new_platform_label || row.new_platform_code}
                    </td>
                    <td className="py-2 pr-4 text-enterprise-600">{row.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};
